import { useCallback, useEffect, useState } from "react";
import { dismissLeftSquad } from "@/lib/leftSquads";

const KEY = "pulse.dissolvedSquads";
const TTL_MS = 24 * 60 * 60 * 1000;

export type DissolvedSquad = {
  id: string;
  name: string;
  dissolvedAt: number;
};

function prune(entries: DissolvedSquad[]): DissolvedSquad[] {
  const cutoff = Date.now() - TTL_MS;
  return entries.filter((e) => e.dissolvedAt >= cutoff);
}

function writeDissolvedSquads(entries: DissolvedSquad[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(prune(entries)));
  } catch {}
  window.dispatchEvent(new CustomEvent("pulse:dissolvedSquads"));
}

export function readDissolvedSquads(): DissolvedSquad[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as DissolvedSquad[];
    const list = Array.isArray(parsed) ? parsed : [];
    const pruned = prune(list);
    if (pruned.length !== list.length) writeDissolvedSquads(pruned);
    return pruned;
  } catch {
    return [];
  }
}

export function recordDissolvedSquad(entry: { id: string; name: string; dissolvedAt?: number }) {
  dismissLeftSquad(entry.id);
  const dissolvedAt = entry.dissolvedAt ?? Date.now();
  const rest = readDissolvedSquads().filter((e) => e.id !== entry.id);
  writeDissolvedSquads([{ id: entry.id, name: entry.name, dissolvedAt }, ...rest]);
}

export function dismissDissolvedSquad(squadId: string) {
  writeDissolvedSquads(readDissolvedSquads().filter((e) => e.id !== squadId));
}

export function useDissolvedSquads() {
  const [dissolvedSquads, setDissolvedSquads] = useState<DissolvedSquad[]>([]);

  const refresh = useCallback(() => setDissolvedSquads(readDissolvedSquads()), []);

  useEffect(() => {
    refresh();
    const h = () => refresh();
    window.addEventListener("pulse:dissolvedSquads", h);
    window.addEventListener("storage", h);
    return () => {
      window.removeEventListener("pulse:dissolvedSquads", h);
      window.removeEventListener("storage", h);
    };
  }, [refresh]);

  return { dissolvedSquads, dismissDissolvedSquad, refresh };
}
